import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../hooks/useWorkspace';
import {
  draftAgentManifest,
  draftTeamManifest,
  installAgentDraft,
  installTeamDraft
} from '../services/api';
import { workspacePath } from '../services/paths';

// BB-2 (docs/build_bot_platform.md §4): the real Agent Builder — the
// bot_builder.md spirit, our way. Natural-language requirement → drafted
// manifest → human review/edit gate → install (mirrors the site_block
// approval pattern, build.md §11). Nothing is written until the owner
// approves the draft. Home: Marketplace module (agent-bot-builder manifest
// module_slug), reachable via the "Build a custom agent" entry point.

const stages = ['Describe', 'Review draft', 'Installed'];

function ManifestField({ label, value, onChange, multiline = false }) {
  const Tag = multiline ? 'textarea' : 'input';

  return (
    <label>
      {label}
      <Tag value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

export default function AgentBuilder() {
  const state = useWorkspace();
  const [stage, setStage] = useState(0);
  const [mode, setMode] = useState('bot'); // 'bot' | 'team'
  const [description, setDescription] = useState('');
  const [draft, setDraft] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [urls, setUrls] = useState('');

  function setDraftField(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  function setNestedDraftField(scope, field, value) {
    setDraft((current) => ({
      ...current,
      [scope]: { ...current[scope], [field]: value }
    }));
  }

  function setSpecialistField(index, field, value) {
    setDraft((current) => ({
      ...current,
      specialists: current.specialists.map((item, idx) => (
        idx === index ? { ...item, [field]: value } : item
      ))
    }));
  }

  function onDraft() {
    setError(null);

    try {
      const urlList = urls.split(/[\n,]/).map((item) => item.trim()).filter(Boolean);
      const next = mode === 'bot'
        ? draftAgentManifest({ description, urls: urlList })
        : draftTeamManifest({ description });

      setDraft(next);
      setStage(1);
    } catch (draftError) {
      setError(draftError.message);
    }
  }

  async function onInstall() {
    setBusy(true);
    setError(null);

    try {
      const installed = mode === 'bot'
        ? await installAgentDraft(draft)
        : await installTeamDraft(draft);

      setResult(installed);
      setStage(2);
    } catch (installError) {
      setError(installError.message);
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setStage(0);
    setDescription('');
    setUrls('');
    setDraft(null);
    setResult(null);
    setError(null);
  }

  return (
    <div className="page narrow">
      <header className="page-header">
        <span className="eyebrow">Agent Builder</span>
        <h1>Describe the specialist your business needs.</h1>
        <p>
          A draft is generated for your review — edit anything, then install.
          Nothing is created until you approve it.
        </p>
      </header>

      <section className="panel wizard">
        <div className="step-row">
          {stages.map((item, index) => (
            <span className={stage >= index ? 'active' : ''} key={item}>
              {index + 1}. {item}
            </span>
          ))}
        </div>

        {error && <div className="chat-error" role="alert">{error}</div>}

        {stage === 0 && (
          <>
            <div className="source-tabs">
              <button
                type="button"
                className={mode === 'bot' ? 'button primary compact' : 'button secondary compact'}
                onClick={() => setMode('bot')}
              >
                Single specialist
              </button>
              <button
                type="button"
                className={mode === 'team' ? 'button primary compact' : 'button secondary compact'}
                onClick={() => setMode('team')}
              >
                Whole team
              </button>
            </div>

            <label>
              What should {mode === 'bot' ? 'this agent' : 'this team'} do?
              <textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Example: Review supplier invoices, track spend against budget, and flag cash-flow risks before month end."
              />
            </label>

            {mode === 'bot' && (
              <label>
                Knowledge source URLs (optional, one per line)
                <textarea
                  value={urls}
                  onChange={(event) => setUrls(event.target.value)}
                  placeholder={'https://example.com/pricing\nhttps://docs.example.com/api'}
                />
              </label>
            )}

            <div className="wizard-actions">
              <button
                className="button primary"
                disabled={!description.trim()}
                onClick={onDraft}
              >
                Draft {mode === 'bot' ? 'bot manifest' : 'team roster'}
              </button>
            </div>
          </>
        )}

        {stage === 1 && draft && mode === 'bot' && (
          <>
            <span className="eyebrow">Draft manifest — review and edit</span>
            <div className="picker-form">
              <ManifestField label="Name" value={draft.name} onChange={(value) => setDraftField('name', value)} />
              <ManifestField label="Role" value={draft.role} onChange={(value) => setDraftField('role', value)} />
              <ManifestField
                label="System prompt"
                value={draft.system_prompt}
                onChange={(value) => setDraftField('system_prompt', value)}
                multiline
              />
              <label>
                Model tier
                <select
                  value={draft.model_tier}
                  onChange={(event) => setDraftField('model_tier', event.target.value)}
                >
                  {(state.modelTiers || []).map((tier) => (
                    <option key={tier.id} value={tier.id}>
                      {tier.name} · {tier.speed} · {tier.cost}
                    </option>
                  ))}
                </select>
              </label>
              {draft.capabilities?.length > 0 && (
                <p className="empty-copy">
                  Detected capabilities: {draft.capabilities.join(', ')}
                </p>
              )}
              {draft.tools?.length > 0 && (
                <p className="empty-copy">Tools: {draft.tools.join(', ')}</p>
              )}
            </div>
            <div className="wizard-actions">
              <button className="button secondary" onClick={() => setStage(0)}>
                Back
              </button>
              <button className="button primary" onClick={onInstall} disabled={busy}>
                {busy ? 'Installing…' : 'Approve and install'}
              </button>
            </div>
          </>
        )}

        {stage === 1 && draft && mode === 'team' && (
          <>
            <span className="eyebrow">Draft roster — review and edit</span>
            <div className="picker-form">
              <ManifestField label="Team name" value={draft.name} onChange={(value) => setDraftField('name', value)} />
              <label>
                Module
                <select
                  value={draft.module}
                  onChange={(event) => setDraftField('module', event.target.value)}
                >
                  {(state.modules || []).map((module) => (
                    <option key={module.slug} value={module.slug}>{module.name}</option>
                  ))}
                </select>
              </label>
              <h4>Lead</h4>
              <ManifestField label="Lead name" value={draft.lead.name} onChange={(value) => setNestedDraftField('lead', 'name', value)} />
              <ManifestField label="Lead role" value={draft.lead.role} onChange={(value) => setNestedDraftField('lead', 'role', value)} />
              <ManifestField
                label="Lead system prompt"
                value={draft.lead.system_prompt}
                onChange={(value) => setNestedDraftField('lead', 'system_prompt', value)}
                multiline
              />
              <h4>Specialists</h4>
              {draft.specialists.map((specialist, index) => (
                <div className="member-row" key={index}>
                  <ManifestField
                    label="Name"
                    value={specialist.name}
                    onChange={(value) => setSpecialistField(index, 'name', value)}
                  />
                  <ManifestField
                    label="Role"
                    value={specialist.role}
                    onChange={(value) => setSpecialistField(index, 'role', value)}
                  />
                </div>
              ))}
            </div>
            <div className="wizard-actions">
              <button className="button secondary" onClick={() => setStage(0)}>
                Back
              </button>
              <button className="button primary" onClick={onInstall} disabled={busy}>
                {busy ? 'Installing…' : 'Approve and install team'}
              </button>
            </div>
          </>
        )}

        {stage === 2 && result && mode === 'bot' && (
          <div className="success-note">
            Installed <b>{result.bot.name}</b> with {result.flow.steps.length} flow step
            {result.sourcesInstalled ? ` and ${result.sourcesInstalled} knowledge source(s)` : ''}.
            <div className="wizard-actions">
              <button className="button secondary compact" onClick={reset}>
                Build another
              </button>
              <Link className="button primary compact" to={workspacePath('bots',state)}>
                Configure in Bots
              </Link>
            </div>
          </div>
        )}

        {stage === 2 && result && mode === 'team' && (
          <div className="success-note">
            Installed team <b>{result.team.name}</b>: {result.lead.name} (Lead) +{' '}
            {result.specialists.length} specialists, wired with a depth-1 consult plan.
            <div className="wizard-actions">
              <button className="button secondary compact" onClick={reset}>
                Build another
              </button>
              <Link className="button primary compact" to={workspacePath('bots',state)}>
                Configure in Bots
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
